import { useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Collapsible from '@/components/Collapsible';
import CardItemView from '@/components/CardItemView';
import rightIcon from '@/assets/chevron_right.svg';
import leftIcon from '@/assets/chevron_left.svg';

const RelatedCardsSection = ({ cards = [], cardId }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const scrollContainerRef = useRef(null);
  const [showLeftButton, setShowLeftButton] = useState(false);
  const [showRightButton, setShowRightButton] = useState(false);
  const [needsScrolling, setNeedsScrolling] = useState(false);
  const hasRestoredScroll = useRef(false);
  const isFirstMount = useRef(true);
  const previousPath = useRef(location.pathname + location.search);
  const previousCardId = useRef(cardId);
  const isRestoringScroll = useRef(false);

  const handleScroll = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;

      // Save scroll position to sessionStorage when user scrolls
      // This ensures the scroll position is saved even if user navigates away
      // Don't save during restoration to prevent overwriting the saved value
      // Only save if scrollLeft > 0 to prevent saving 0 when container is at the start
      if (cardId && hasRestoredScroll.current && !isRestoringScroll.current && scrollLeft > 0) {
        const currentPath = location.pathname + location.search;
        sessionStorage.setItem(`relatedCardsScroll_${currentPath}_${cardId}`, scrollLeft.toString());
      }

      // Check if scrolling is actually needed
      const hasOverflow = scrollWidth > clientWidth + 10; // 10px threshold
      setNeedsScrolling(hasOverflow);

      if (hasOverflow) {
        setShowLeftButton(scrollLeft > 0);
        // Show right button only if there's more content to scroll right
        setShowRightButton(scrollLeft + clientWidth < scrollWidth - 10);
      } else {
        setShowLeftButton(false);
        setShowRightButton(false);
      }
    }
  }, [cardId, location.pathname, location.search]);

  const scrollRight = useCallback(() => {
    if (scrollContainerRef.current) {
      // Scroll by 600px or a similar value that works well with your card width and gap
      scrollContainerRef.current.scrollBy({
        left: 600,
        behavior: 'smooth'
      });
    }
  }, []);
  
  const scrollLeft = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -600,
        behavior: 'smooth'
      });
    }
  }, []);

  // Save scroll position when user clicks on a related card
  const handleCardClick = useCallback((e) => {
    // Only save if clicking on a card link (not on navigation buttons or other elements)
    // Remove dependency on hasRestoredScroll.current to ensure scroll position is saved even if restoration hasn't completed
    // Only save if scrollLeft > 0 to prevent saving 0 when container is at the start
    const target = e.target.closest('a');
    if (target && scrollContainerRef.current && cardId) {
      const scrollLeft = scrollContainerRef.current.scrollLeft;
      if (scrollLeft > 0) {
        const currentPath = location.pathname + location.search;
        sessionStorage.setItem(`relatedCardsScroll_${currentPath}_${cardId}`, scrollLeft.toString());
      }
    }
  }, [cardId, location.pathname, location.search]);
  
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      // Initial check with a small delay to ensure content is rendered
      const checkScrollNeeded = () => {
        handleScroll();
      };

      // Check immediately
      checkScrollNeeded();

      // Check again after a short delay to ensure content is fully rendered
      const timeoutId = setTimeout(checkScrollNeeded, 100);

      scrollContainer.addEventListener('scroll', handleScroll);

      // Recheck when window resizes
      window.addEventListener('resize', handleScroll);

      return () => {
        clearTimeout(timeoutId);
        scrollContainer.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
      };
    }
  }, [handleScroll]);

  // Additional effect to check when cards change
  useEffect(() => {
    if (scrollContainerRef.current && cards.length > 0) {
      // Use requestAnimationFrame to ensure DOM is updated
      // Only update button states during restoration, don't save scroll position
      requestAnimationFrame(() => {
        if (!scrollContainerRef.current) {
          return;
        }
        if (!isRestoringScroll.current) {
          handleScroll();
        } else {
          // Only update button states during restoration
          const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
          const hasOverflow = scrollWidth > clientWidth + 10;
          setNeedsScrolling(hasOverflow);
          if (hasOverflow) {
            setShowLeftButton(scrollLeft > 0);
            setShowRightButton(scrollLeft + clientWidth < scrollWidth - 10);
          } else {
            setShowLeftButton(false);
            setShowRightButton(false);
          }
        }
      });
    }
  }, [cards, handleScroll]);

  // Reset restore flag when path or cardId changes
  useEffect(() => {
    const currentPath = location.pathname + location.search;
    const hasPathChanged = previousPath.current !== currentPath;
    const hasCardIdChanged = previousCardId.current !== cardId;
    
    if (hasPathChanged || hasCardIdChanged) {
      hasRestoredScroll.current = false;
      previousPath.current = currentPath;
      previousCardId.current = cardId;
    }
  }, [cardId, location.pathname, location.search]);

  // Detect page refresh and clear saved scroll position
  useEffect(() => {
    const currentPath = location.pathname + location.search;
    const hasPathChanged = previousPath.current !== currentPath;
    const hasCardIdChanged = previousCardId.current !== cardId;
    
    // Check if this is a page refresh (first mount AND no path/cardId change AND navigation type is reload)
    const isPageRefresh = isFirstMount.current && 
      !hasPathChanged && 
      !hasCardIdChanged &&
      typeof performance !== 'undefined' && 
      performance.navigation && 
      performance.navigation.type === 1;
    
    if (isPageRefresh && cardId) {
      // Clear saved scroll position on refresh
      sessionStorage.removeItem(`relatedCardsScroll_${currentPath}_${cardId}`);
      isFirstMount.current = false;
    } else if (isFirstMount.current) {
      // Mark that we've passed first mount
      isFirstMount.current = false;
    }
  }, [cardId, location.pathname, location.search]);

  // Handle popstate event to immediately reset scroll position
  // This prevents browser default restoration from causing animation
  useEffect(() => {
    const handlePopState = () => {
      // Try to find container immediately, or wait for it to be available
      const findAndResetContainer = () => {
        const container = scrollContainerRef.current || document.querySelector('.cards-scroll-container');
        if (container) {
          // Immediately remove scroll-smooth class and set scroll-behavior to auto to prevent animation
          container.classList.remove('scroll-smooth');
          container.style.setProperty('scroll-behavior', 'auto', 'important');
          // Force a reflow to ensure the style change is applied
          void container.offsetHeight;
          // Immediately reset to 0 to prevent browser from restoring wrong position
          container.scrollTo({ left: 0, behavior: 'instant' });
        } else {
          // Container not found yet, try again after a short delay
          requestAnimationFrame(() => {
            setTimeout(findAndResetContainer, 0);
          });
        }
      };
      
      // Try immediately
      findAndResetContainer();
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Restore scroll position when component mounts or cardId changes
  useEffect(() => {
    if (scrollContainerRef.current && cardId && cards.length > 0 && !hasRestoredScroll.current) {
      // Use window.location to get the most current path, avoiding React Router delay
      const currentPath = window.location.pathname + window.location.search;
      const currentCardId = window.location.pathname.split('/').pop();
      const effectiveCardId = currentCardId || cardId;
      const savedScroll = sessionStorage.getItem(`relatedCardsScroll_${currentPath}_${effectiveCardId}`);
      
      if (savedScroll) {
        const scrollLeft = parseInt(savedScroll, 10);
        const scrollContainer = scrollContainerRef.current;
        
        // Set restoration flag to prevent handleScroll from saving during restoration
        hasRestoredScroll.current = false;
        isRestoringScroll.current = true;
        
        // Remove scroll-smooth class and set scroll-behavior to auto to prevent animation
        // Do this IMMEDIATELY and SYNCHRONOUSLY before any scroll operations
        const hadScrollSmooth = scrollContainer.classList.contains('scroll-smooth');
        scrollContainer.classList.remove('scroll-smooth');
        scrollContainer.style.setProperty('scroll-behavior', 'auto', 'important');
        // Force a reflow to ensure the style change is applied before scrolling
        void scrollContainer.offsetHeight;
        
        // Reset scroll to 0 first to avoid browser default restoration interference
        // Use scrollTo with behavior: 'instant' to ensure no animation
        scrollContainer.scrollTo({ left: 0, behavior: 'instant' });
        
        // Function to attempt restoration
        const attemptRestore = (attempts = 0) => {
          if (scrollContainer && scrollContainer.scrollWidth > 0) {
            // Content is ready, restore scroll position using scrollTo with behavior: 'instant'
            scrollContainer.scrollTo({ left: scrollLeft, behavior: 'instant' });
            
            // Release restoration flag after a delay to ensure restoration is complete
            setTimeout(() => {
              const actualScrollLeft = scrollContainer.scrollLeft;
              if (Math.abs(actualScrollLeft - scrollLeft) < 50) {
                // Scroll position is correctly restored
                // Restore scroll-smooth class if it was present
                if (hadScrollSmooth) {
                  scrollContainer.classList.add('scroll-smooth');
                }
                scrollContainer.style.scrollBehavior = '';
                hasRestoredScroll.current = true;
                isRestoringScroll.current = false;
                handleScroll();
              } else {
                // Scroll position not correctly restored, try again
                scrollContainer.scrollTo({ left: scrollLeft, behavior: 'instant' });
                setTimeout(() => {
                  const retryScrollLeft = scrollContainer.scrollLeft;
                  if (Math.abs(retryScrollLeft - scrollLeft) < 50) {
                    // Now correctly restored
                    if (hadScrollSmooth) {
                      scrollContainer.classList.add('scroll-smooth');
                    }
                    scrollContainer.style.scrollBehavior = '';
                    hasRestoredScroll.current = true;
                    isRestoringScroll.current = false;
                    handleScroll();
                  } else {
                    // Still not restored, give up but keep isRestoringScroll to prevent saving
                    if (hadScrollSmooth) {
                      scrollContainer.classList.add('scroll-smooth');
                    }
                    scrollContainer.style.scrollBehavior = '';
                    hasRestoredScroll.current = true;
                    // Keep isRestoringScroll.current = true to prevent saving 0
                  }
                }, 100);
              }
            }, 50);
          } else if (attempts < 10) {
            // Content not ready yet, try again after a short delay
            setTimeout(() => {
              attemptRestore(attempts + 1);
            }, 100);
          } else {
            // Give up after 10 attempts (1 second)
            // Restore scroll-smooth class if it was present
            if (hadScrollSmooth) {
              scrollContainer.classList.add('scroll-smooth');
            }
            scrollContainer.style.scrollBehavior = '';
            hasRestoredScroll.current = true;
            // Keep isRestoringScroll.current = true to prevent saving 0
          }
        };
        
        // Wait for DOM to be ready and cards to be rendered
        // Use immediate restoration similar to InfiniteScrollRestoration
        requestAnimationFrame(() => {
          attemptRestore();
          setTimeout(() => {
            attemptRestore();
          }, 50);
        });
      } else {
        // No saved scroll position for this page
        // Set isRestoringScroll to prevent handleScroll from saving during initial render
        // Delay setting hasRestoredScroll to ensure cards are fully rendered
        isRestoringScroll.current = true;
        setTimeout(() => {
          hasRestoredScroll.current = true;
          isRestoringScroll.current = false;
        }, 200);
      }
    }
  }, [cardId, cards, location.pathname, location.search, handleScroll]);
  
  // If no cards, don't render section
  if (!cards || cards.length === 0) {
    return null;
  }
  
  return (
    <>
      <style>
        {`
          .cards-scroll-container {
            -ms-overflow-style: none;  /* IE and Edge */
            scrollbar-width: none;  /* Firefox */
          }
          
          .cards-scroll-container::-webkit-scrollbar {
            display: none;  /* Chrome, Safari and Opera */
          }
        `}
      </style>
      <Collapsible title={t('purchase.success.related_recommendations')} defaultOpen className="border-0 rounded-none border-t">
        {/* Cards List */}
        <div className="relative bg-white overflow-hidden">
          <div
            ref={scrollContainerRef}
            className="cards-scroll-container overflow-x-auto pb-4 -mx-4 px-4 flex gap-6 scroll-smooth"
            onScroll={handleScroll}
            onClick={handleCardClick}
          >
            {cards.map((card) => (
              <CardItemView
                key={card.id}
                id={card.id}
                className="w-[204px] flex-shrink-0"
                name={card.localizedRawName}
                type={card.type === 'SINGLE_CARD' ? 'PSA_SINGLE_CARD' : 'CARD_BOX'}
                image={card.images && card.images.length > 0 ? card.images[0].url : ""}
                brand={card.brand?.localizedName}
                price={(() => {
                  const vals = [
                    card.stats?.lowestPriceOnSale,
                    card.stats?.lowestPriceOnSaleOfficial,
                  ].filter(v => typeof v === 'number' && v > 0);
                  return vals.length ? Math.min(...vals) : 0;
                })()}
                orders={card.stats?.salesVolume}
                code={card.code}
                rawCode={card.rawCode}
                set={card.set}
                rarityInfo={card.rarityInfo}
              />
            ))}
          </div>

          {/* Gradient Overlay - Right */}
          {needsScrolling && showRightButton && (
            <div className="absolute right-0 top-0 bottom-0 w-[105px] bg-gradient-to-l from-white to-white/0" />
          )}

          {/* Gradient Overlay - Left */}
          {needsScrolling && showLeftButton && (
            <div className="absolute left-0 top-0 bottom-0 w-[105px] bg-gradient-to-r from-white to-white/0" />
          )}

          {/* Right Navigation Button */}
          {needsScrolling && showRightButton && (
            <button
              onClick={scrollRight}
              className="absolute right-0 top-1/2 -translate-y-1/2 w-12 h-12 p-4 bg-white/90 rounded-[48px] shadow-[0px_4px_8px_0px_rgba(0,0,0,0.08)] border border-[#eeeeee] flex items-center justify-center cursor-pointer hover:bg-white hover:shadow-[0px_4px_12px_0px_rgba(0,0,0,0.12)] hover:scale-110 transition-all z-10"
            >
              <img src={rightIcon} alt="Next" className="w-4 h-4" />
            </button>
          )}

          {/* Left Navigation Button */}
          {needsScrolling && showLeftButton && (
            <button
              onClick={scrollLeft}
              className="absolute left-0 top-1/2 -translate-y-1/2 w-12 h-12 p-4 bg-white/90 rounded-[48px] shadow-[0px_4px_8px_0px_rgba(0,0,0,0.08)] border border-[#eeeeee] flex items-center justify-center cursor-pointer hover:bg-white hover:shadow-[0px_4px_12px_0px_rgba(0,0,0,0.12)] hover:scale-110 transition-all z-10"
            >
              <img src={leftIcon} alt="Previous" className="w-4 h-4" />
            </button>
          )}
        </div>
      </Collapsible>
    </>
  );
};

export default RelatedCardsSection; 